import { CHURCHES, churchTag } from './churches';
import type { Orient, Region, Tag, TagCategory } from './model';

/** Turn user input ("#Summer Retreat", "YA") into a canonical tag name, resolving aliases. */
export function normalizeTag(input: string, aliases: Record<string, string> = DEFAULT_ALIASES): string | null {
  const raw = input
    .trim()
    .toLowerCase()
    .replace(/^#+/, '')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (!raw) return null;
  const alias = aliases[raw.replace(/-/g, '')] ?? aliases[raw];
  return (alias ?? raw).slice(0, 48);
}

export function orientOf(width: number, height: number): Orient {
  const ar = width / height;
  return ar > 1.05 ? 'h' : ar < 0.95 ? 'v' : 'sq';
}

/** The capture year as a tag, e.g. "2026". */
export function yearTag(isoDate: string): string {
  return String(new Date(isoDate).getUTCFullYear());
}

export const CATEGORY_LABEL: Record<TagCategory, string> = {
  year: 'Year',
  region: 'Region',
  event: 'Event',
  church: 'Church',
  fellowship: 'Fellowship',
  service: 'Service',
  '': 'Tag',
};

export const REGIONS: readonly Region[] = ['East', 'Central', 'West'];

/**
 * Fixed lists shared by every JBCH USA app: kept in the shared users table (`LIST#<category>`), not created from the app.
 * Churches come from the production `churches` table (infra/scripts/load-lists.ts).
 */
export const SHARED_LIST_CATEGORIES: readonly TagCategory[] = ['region', 'church', 'fellowship', 'service'];

/** The region tag for a profile region ('East' → 'east'). */
export function regionTag(region: Region): string {
  return region.toLowerCase();
}

/** Starting tag vocabulary; the media team can add more. Years are created automatically. */
export const DEFAULT_TAGS: Tag[] = [
  ...(
    [
      ['east', 'East'],
      ['central', 'Central'],
      ['west', 'West'],
    ] as const
  ).map(([name, label]) => ({ name, label, category: 'region' as const, aliases: [] })),
  ...(
    [
      ['summer-retreat', 'Summer Retreat'],
      ['winter-retreat', 'Winter Retreat'],
      ['young-adult-retreat', 'Young Adult Retreat'],
      ['young-adult-officers-retreat', 'Young Adult Officers Retreat'],
      ['officers-retreat', 'Officers Retreat'],
      ['teachers-retreat', 'Teachers Retreat'],
      ['fathers-group-retreat', 'Fathers Group Retreat'],
      ['av-media-retreat', 'AV/Media Retreat'],
      ['yg-summer-camp', 'YG Summer Camp'],
    ] as const
  ).map(([name, label]) => ({ name, label, category: 'event' as const, aliases: [] })),
  ...(
    [
      ['fathers', 'Fathers'],
      ['mothers', 'Mothers'],
      ['young-adults', 'Young Adults'],
      ['seniors', 'Seniors'],
    ] as const
  ).map(([name, label]) => ({ name, label, category: 'fellowship' as const, aliases: [] })),
  ...(
    [
      ['choir', 'Choir'],
      ['translation', 'Translation'],
      ['media', 'Media'],
      ['av', 'AV'],
      ['church-school', 'Church School'],
      ['news-letter', 'News Letter'],
      ['cafe', 'Cafe'],
    ] as const
  ).map(([name, label]) => ({ name, label, category: 'service' as const, aliases: [] })),
  ...CHURCHES.map((c) => ({ name: churchTag(c.name), label: c.name, category: 'church' as const, aliases: [], churchId: c.id, region: c.region })),
];

export const DEFAULT_ALIASES: Record<string, string> = {
  ya: 'young-adults',
  youngadults: 'young-adults',
  ny: 'new-york',
  nj: 'new-jersey',
  atl: 'atlanta',
  chi: 'chicago',
  sf: 'san-francisco',
  vegas: 'las-vegas',
  cs: 'church-school',
  summer: 'summer-retreat',
  winter: 'winter-retreat',
  yg: 'yg-summer-camp',
  ygcamp: 'yg-summer-camp',
  summercamp: 'yg-summer-camp',
  yaretreat: 'young-adult-retreat',
  yaofficers: 'young-adult-officers-retreat',
  officers: 'officers-retreat',
  teachers: 'teachers-retreat',
  fathersretreat: 'fathers-group-retreat',
  avmedia: 'av-media-retreat',
  mediaretreat: 'av-media-retreat',
  eastcoast: 'east',
  westcoast: 'west',
  midwest: 'central',
  praiseteam: 'choir',
  praise: 'choir',
  translators: 'translation',
  translator: 'translation',
  newsletter: 'news-letter',
  mediateam: 'media',
};

/** Category of a tag name given a registry; 4-digit numbers are years. */
export function categoryOf(name: string, registry: Map<string, Tag>): TagCategory {
  if (/^(19|20)\d\d$/.test(name)) return 'year';
  return registry.get(name)?.category ?? '';
}

/**
 * "Update tags" when a photo moves between albums (or is added to one): only album default tags change. Removes the
 * old album's defaults the photo has (unless the destination has them too) and adds the destination's defaults it lacks.
 */
export function albumTagChange(photoTags: readonly string[], fromDefaults: readonly string[], toDefaults: readonly string[]): { add: string[]; remove: string[] } {
  return {
    remove: fromDefaults.filter((t) => photoTags.includes(t) && !toDefaults.includes(t)),
    add: toDefaults.filter((t) => !photoTags.includes(t)),
  };
}
